import { Box, Checkbox, HStack, Radio, RadioGroup, Select, Text, Tooltip } from '@chakra-ui/react'
import type { AddonProduct, InsuranceParamsDto, Offer } from 'BrokerTsLib/src/dtos'
import { Risk } from 'BrokerTsLib/src/enums'
import { addMonths, format } from 'date-fns'
import { useEffect, useState } from 'react'

import { useInsuranceParams } from '../../../context/InsuranceParamsProvider'
import { useOffersAddonContext } from '../../../context/OffersAddonProvider'
import { useOffersVehicleContext } from '../../../context/OffersVehicleProvider'
import { useSetOfferPaymentFrequency } from '../../../hooks/useOffer'
import { useChangePrice, useGetAddonParamValues, useSelectOfferAddon } from '../../../hooks/useOfferAddon'
import { QuestionMarkIcon } from '../../QuestionMarkIcon'
import { OfferPart } from './OfferPart'

export interface GapDetailsProps {
  offer: Offer
}

export const GapDetails = ({ offer }: GapDetailsProps) => {
  const gapPart = offer.parts.find(x => x.riskId == Risk.GAP)!

  const { insuranceParams, isDisabled } = useInsuranceParams()
  const insurancePramsDto = insuranceParams as InsuranceParamsDto

  const { recalculateOffers: recalculate } = useOffersVehicleContext()
  const { offers: addonOffers, refetchOffers: refetchAddonOffers, updateOffer: updateAddonOffer } = useOffersAddonContext()
  const allGapOffers = addonOffers.filter(x => x.riskId == Risk.GAP) ?? []

  const followingGapOffers = allGapOffers.filter(x => x.params?.isFollowGap)
  const followingGapOffer = followingGapOffers.find(x => x.linkedOfferId?.includes(offer.offerId) ?? false)
    ?? (followingGapOffers.find(x => (60 - parseInt(x.params?.parameterValues.find(p => p.name == 'Term')?.value ?? '0')) == (gapPart?.term ?? 0))
      ?? (followingGapOffers.length > 0 ? followingGapOffers[0] : null))

  const replacingGapOffers = allGapOffers.filter(x => !x.params?.isFollowGap)
  const replacingGapOffer = replacingGapOffers.find(x => x.linkedOfferId?.includes(offer.offerId) ?? false)
    ?? (replacingGapOffers.find(x => x.params?.parameterValues.find(p => p.name == 'Term')?.value == gapPart?.term)
      ?? (replacingGapOffers.length > 0 ? replacingGapOffers[0] : null))
  const [gapType, setGapType] = useState<'following' | 'replacing'>((replacingGapOffer?.linkedOfferId?.includes(offer.offerId) ?? false) ? 'replacing' : 'following')
  const [checkedFollowingGap, setCheckedFollowingGap] = useState(followingGapOffer?.linkedOfferId?.includes(offer.offerId) ?? false)
  const [isLoadingGapOffers, setIsLoadingGapOffers] = useState(false)

  const [followingGapPossibleTerms, setFollowingGapPossibleTerms] = useState<AddonProduct[]>([])
  const [replacingGapPossibleTerms, setNotFollowingGapPossibleTerms] = useState<AddonProduct[]>([])

  const { mutateAsync: getAddonParamValues } = useGetAddonParamValues()
  const { mutateAsync: selectOfferAddon } = useSelectOfferAddon()
  const { mutateAsync: updatePaymentFrequency } = useSetOfferPaymentFrequency()
  const { mutateAsync: changePrice } = useChangePrice()

  useEffect(() => {
    if (followingGapOffer)
      getAddonParamValues({
        mainOfferId: offer.offerId,
        addonOfferId: followingGapOffer.offerId,
        paramName: 'Term'
      }).then(({ data }) => setFollowingGapPossibleTerms(data))
  }, [followingGapOffer?.offerId])

  useEffect(() => {
    if (replacingGapOffer)
      getAddonParamValues({
        mainOfferId: offer.offerId,
        addonOfferId: replacingGapOffer.offerId,
        paramName: 'Term'
      }).then(({ data }) => setNotFollowingGapPossibleTerms(data))
  }, [replacingGapOffer?.offerId])

  const handleChangeGapType = (value: 'following' | 'replacing') => {
    if (isDisabled)
      return

    setGapType(value)

    const selectedOffer = value == 'following' ? null : replacingGapOffer

    handleSelectAddonOffer(selectedOffer?.offerId, selectedOffer?.params?.parameterValues.find(x => x.name == 'Term')?.value)
      .then(() => recalculate([offer.offerId]))
  }

  const handleSelectAddonOffer = (addonOfferId?: number, term?: string) => {
    setIsLoadingGapOffers(true)
    return selectOfferAddon({ mainOfferId: offer.offerId, addonOfferId, term })
      .then(() => refetchAddonOffers({})
        .finally(() => setIsLoadingGapOffers(false)))
  }

  useEffect(() => {
    setCheckedFollowingGap(followingGapOffer?.linkedOfferId?.includes(offer.offerId) ?? false)
  }, [followingGapOffer])

  useEffect(() => {
    setGapType((replacingGapOffer?.linkedOfferId?.includes(offer.offerId) ?? false) ? 'replacing' : 'following')
  }, [replacingGapOffer])

  const linkedOfferAddon = allGapOffers.find(x => x.linkedOfferId?.includes(offer.offerId))
  const defendGapEditPriceTooltip = 'Nastavení ceny má vliv na provizi. Při zadání nejnižší možné částky je provize 0%'

  return (
    <RadioGroup name="gap-type" value={gapType} onChange={handleChangeGapType}>
      <Box display='grid' rowGap={1} sx={{
        p: 2,
        pr: 0,
        gap: 3,
        borderBottom: '1px solid',
        borderBottomColor: 'neutral.50',
        _last: {
          borderBottom: 'none',
        },
      }}>

        {/* Nahrazující */}
        {replacingGapOffer &&
          <Box>
            <Text sx={{ fontSize: 'sm', mb: 2 }}>GAP</Text>

            {/* GAP pojistovny */}
            <OfferPart
              key={gapPart.partId}
              insurer={offer.insurerId}
              riskId={gapPart.riskId}
              label={
                <Radio
                  size="sm"
                  value="following"
                  disabled={isDisabled}>{offer.insurerName}
                </Radio>
              }
              errors={gapPart.errors}
              price={gapPart.price}
            // value={gapPart.nameNew ?? gapPart.name}
            />

            {/* nahrazujici GAP */}
            <OfferPart
              riskId={replacingGapOffer.riskId}
              insurer={replacingGapOffer.insurerId}
              key={replacingGapOffer.offerId}
              label={
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto auto',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'no-wrap',
                }}>
                  <Radio
                    size="sm"
                    value="replacing"
                    disabled={isDisabled}
                  >
                    {replacingGapOffer?.insurerName}
                  </Radio>

                  <Select
                    variant='filled'
                    size="xs"
                    disabled={isDisabled}
                    value={replacingGapOffer.params?.parameterValues.find(x => x.name == 'Term')?.value}
                    onChange={e => handleSelectAddonOffer(replacingGapOffer?.offerId, e.target.value)}
                  >
                    {replacingGapPossibleTerms.map(x => <option key={x.value} value={x.value}>{x.text}</option>)}
                  </Select>
                </Box>
              }
              unavailableText='Nelze sjednat'
              price={replacingGapOffer.pricePerYear}
              // valid={true}
              // errors={replacingGapOffer.errors}
              editableProps={{
                isEditable: !!replacingGapOffer.priceMin && !!replacingGapOffer.priceMax && gapType == 'replacing' && !isDisabled,
                priceMin: replacingGapOffer.priceMin,
                priceMax: replacingGapOffer.priceMax,
                price: replacingGapOffer.pricePerYear,
                onChange: (price: number, successAction: () => void, errorAction: (error: string) => void) => {
                  changePrice({ offerId: replacingGapOffer!.offerId, price })
                    .then(({ data: updatedOffer }) => {
                      successAction()
                      updateAddonOffer(updatedOffer)
                    }).catch((reason) => {
                      let error = 'Nelze uložit změnu ceny'
                      if (reason.response.status == 400) {
                        error = reason.response.data
                      }
                      errorAction(error)
                    })
                },
                tooltip: defendGapEditPriceTooltip
              }}
              errors={replacingGapOffer.errors}
            />
          </Box>
        }

        {/* navazujici na GAP pojistovny */}
        {gapPart.valid && followingGapOffer && gapType == 'following' &&
          <Box>
            <HStack sx={{ fontSize: 'sm', mb: 2 }}>
              <Tooltip label={<Text>Navazující od {format(addMonths(new Date(insurancePramsDto.generalParams.insuranceStartDate!), gapPart.term ?? 0), 'd. M. yyyy')}</Text>} placement="top" hasArrow>
                <Text>GAP navazující</Text>

                {/* <QuestionMarkIcon /> */}
              </Tooltip>
            </HStack>

            <OfferPart
              riskId={gapPart.riskId}
              insurer={followingGapOffer.insurerId}
              label={<Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto auto auto',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'no-wrap',
                  gap: 2
                }}>

                <Checkbox
                  size="sm"
                  sx={{ flexBasis: '100%' }}
                  isChecked={checkedFollowingGap}
                  disabled={isDisabled}
                  onChange={e => {
                    setCheckedFollowingGap(e.target.checked)

                    if (e.target.checked)
                      handleSelectAddonOffer(followingGapOffer?.offerId, followingGapOffer?.params?.parameterValues.find(x => x.name == 'Term')?.value)

                    else
                      handleSelectAddonOffer()
                  }}
                >
                  {followingGapOffer.insurerName}
                </Checkbox>

                <Select
                  variant='filled'
                  size="xs"
                  disabled={isDisabled}
                  value={followingGapOffer.params?.parameterValues.find(x => x.name == 'Term')?.value}
                  onChange={e => handleSelectAddonOffer(followingGapOffer?.offerId, e.target.value)}
                >
                  {followingGapPossibleTerms.map(x => <option key={x.value} value={x.value}>{x.text}</option>)}
                </Select>

                {followingGapOffer.productDescription &&
                  <Tooltip label={followingGapOffer.productDescription} placement="top" hasArrow>
                    <QuestionMarkIcon />
                  </Tooltip>}
              </Box>}
              price={followingGapOffer.pricePerYear}
              unavailableText='Nelze sjednat'
              editableProps={{
                isEditable: !!followingGapOffer.priceMin && !!followingGapOffer.priceMax && checkedFollowingGap && !isDisabled,
                priceMin: followingGapOffer.priceMin,
                priceMax: followingGapOffer.priceMax,
                price: followingGapOffer.pricePerYear,
                onChange: (price: number, successAction: () => void, errorAction: (error: string) => void) => {
                  changePrice({ offerId: followingGapOffer!.offerId, price })
                    .then(({ data: updatedOffer }) => {
                      successAction()
                      updateAddonOffer(updatedOffer)
                    }).catch((reason) => {
                      let error = 'Nelze uložit změnu ceny'
                      if (reason.response.status == 400) {
                        error = reason.response.data
                      }
                      errorAction(error)
                    })
                },
                tooltip: defendGapEditPriceTooltip
              }}
              errors={followingGapOffer.errors}
            />
          </Box>
        }
      </Box>
    </RadioGroup>
  )
}
